import { Purchases, ReservedCustomerAttribute } from '@revenuecat/purchases-js';

// RevenueCat configuration
const REVENUECAT_API_KEY = process.env.REACT_APP_REVENUECAT_API_KEY || '';

let purchasesInstance: Purchases | null = null;

/**
 * Initialize RevenueCat SDK
 * Call this once when the app starts or when user logs in
 * 
 * IMPORTANT: The userId parameter is sent to RevenueCat as the appUserId.
 * This links all purchases and subscriptions to your user's ID.
 * 
 * @param userId - Your application's unique user ID (NOT RevenueCat's ID)
 * @param email - Optional user email for better customer identification in RevenueCat
 * @returns Configured Purchases instance
 */
export const initializeRevenueCat = (userId: string, email?: string): Purchases => {
  try {
    // Validate API key
    if (!REVENUECAT_API_KEY) {
      console.error('RevenueCat API key is missing. Please check your .env file.');
      console.error('Environment variables available:', Object.keys(process.env).filter(k => k.startsWith('REACT_APP_')));
      throw new Error('RevenueCat API key is not configured');
    }

    // Validate user ID
    if (!userId) {
      console.error('User ID is required for RevenueCat initialization');
      throw new Error('User ID is required');
    }

    console.log('=== RevenueCat Initialization ===');
    console.log('API Key present:', REVENUECAT_API_KEY.substring(0, 10) + '...');
    console.log('App User ID (your user ID):', userId);
    if (email) {
      console.log('User Email:', email);
    }
    console.log('This user ID will be used to identify the customer in RevenueCat');

    // Always close any existing instance and re-configure with the real userId.
    // Using changeUser() only adds the userId as an alias, meaning the
    // originalAppUserId in RevenueCat remains the anonymous ID. Re-calling
    // configure() with the real userId ensures it becomes the originalAppUserId.
    if (purchasesInstance) {
      try {
        purchasesInstance.close();
      } catch (e) {
        console.warn('Error closing previous RC instance:', e);
      }
      purchasesInstance = null;
    }

    purchasesInstance = Purchases.configure({
      apiKey: REVENUECAT_API_KEY,
      appUserId: userId
    });
    console.log('✓ RevenueCat configured with App User ID:', userId);

    // Set user email for better identification in RevenueCat dashboard
    if (email) {
      purchasesInstance.setAttributes({ [ReservedCustomerAttribute.Email]: email });
      console.log('✓ User email set in RevenueCat:', email);
    }

    return purchasesInstance;
  } catch (error) {
    console.error('Failed to initialize RevenueCat:', error);
    throw error;
  }
};

/**
 * Get the configured Purchases instance
 */
export const getPurchasesInstance = (): Purchases | null => {
  return purchasesInstance;
};

/**
 * Completely reset and reconfigure RevenueCat with a new user ID
 * Use this when we detect an anonymous ID but have a logged-in user
 */
export const resetAndReconfigureRevenueCat = (userId: string): never => {
  console.log('=== Resetting RevenueCat ===');
  console.log('Reason: Detected anonymous ID, forcing reconfiguration with user ID:', userId);
  
  // Close existing instance
  if (purchasesInstance) {
    try {
      purchasesInstance.close();
      console.log('✓ Closed existing RevenueCat instance');
    } catch (error) {
      console.error('Error closing RevenueCat instance:', error);
    }
    purchasesInstance = null;
  }
  
  // Force page reload to completely reset RevenueCat SDK state
  console.log('⚠️ Page reload required to reset RevenueCat SDK');
  console.log('Storing user ID for post-reload initialization...');
  sessionStorage.setItem('rcTransferUserId', userId);
  
  // Reload the page to reset SDK
  window.location.reload();
  
  // This won't execute due to reload, but TypeScript needs it
  throw new Error('Page should have reloaded');
};

/**
 * Check if we need to complete a user ID transfer after page reload
 */
export const checkPendingTransfer = (): string | null => {
  const pendingUserId = sessionStorage.getItem('rcTransferUserId');
  if (pendingUserId) {
    console.log('✓ Found pending user ID transfer:', pendingUserId);
    sessionStorage.removeItem('rcTransferUserId');
  }
  return pendingUserId;
};

/**
 * Clean up RevenueCat on logout
 */
export const logoutRevenueCat = (): void => {
  try {
    if (purchasesInstance) {
      console.log('Cleaning up RevenueCat instance');
      purchasesInstance.close();
      purchasesInstance = null;
      console.log('RevenueCat instance cleaned up');
    }
  } catch (error) {
    console.error('Failed to logout from RevenueCat:', error);
  }
};

/**
 * Check if RevenueCat is initialized
 */
export const isRevenueCatInitialized = (): boolean => {
  return purchasesInstance !== null;
};
